import React from 'react';

import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import ActionButton, { ActionButtonVariants } from 'components/action-button';
import useEffectOnce from 'hooks/use-effect-once';
import { CustomEventNames, EventTypes, useMParticleContext } from 'state/mParticle';
import { routes } from 'utils/routing';

import NotFoundImage from './not-found-image';

const NotFoundContainer = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  height: calc(100vh - 140px);
  justify-content: center;
  margin-top: 0;
  padding: 0 3rem;

  ${Styles.desktop`
    height: calc(100vh - 275px - 12rem);
    margin-top: 12rem;
  `}
`;

const StyledNotFoundImage = styled(NotFoundImage)`
  margin: 0 0 0 -10%;
  max-width: ${Styles.layout.smallerSectionMaxWidth};
`;

const Content = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  margin: 1.5rem 0.5rem;
  text-align: center;

  h1 {
    color: ${Styles.color.grey.one};
    font-family: ${Styles.fontFamily.base};
    font-weight: ${Styles.fontWeight.heavy};
    line-height: 1;
    text-transform: uppercase;
  }

  p {
    color: ${Styles.color.grey.two};
    line-height: 1;
    margin: 0 0 1rem 0;
  }

  button {
    margin: 2rem 0 0;
  }
`;

const NotFound = () => {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const mParticle = useMParticleContext();

  useEffectOnce(() => {
    mParticle.logEvent(CustomEventNames.PAGE_NOT_FOUND, EventTypes.Other);
  });

  return (
    <div>
      <Helmet title={formatMessage({ id: 'pageNotFound' })} />
      <NotFoundContainer>
        <StyledNotFoundImage />
        <Content>
          <h1>{formatMessage({ id: 'somethingMissingHere' })}</h1>
          <p>{formatMessage({ id: 'pageMoved' })}</p>
          <ActionButton onClick={() => navigate(routes.base)}>
            {formatMessage({ id: 'homepage' })}
          </ActionButton>
          <ActionButton variant={ActionButtonVariants.OUTLINE} onClick={() => navigate(0)}>
            {formatMessage({ id: 'reload' })}
          </ActionButton>
        </Content>
      </NotFoundContainer>
    </div>
  );
};

export default NotFound;
